import React from 'react';
import endsWith from 'lodash/endsWith';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MobileDrawer from '@/components/mobile/MobileDrawer';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';
import { HOT_CITIES } from '@/constants/data/city';
import { NATION_WIDE } from '@/constants/city';
import {
  CascadeOption,
  getSelectedOptions,
  Option,
} from '@/components/form/cascade-select/CascadeSelect';

interface CityPickerProps {
  onCitySelect: (selectValue: Option, selectOptions: Option[]) => void;
  onClose: () => void;
  options: CascadeOption[];
  cityPickerOpen: boolean;
  value?: Option;
  showNationWide?: boolean;
}

const MobileCityPicker = (props: CityPickerProps) => {
  const isMobile = useClintMediaQuery();
  const {
    value,
    onCitySelect,
    options,
    cityPickerOpen,
    onClose,
    showNationWide = true,
  } = props;
  const [searchValue, setSearchValue] = React.useState('');

  const handleSelect = (selectedValue: Option) => {
    const selectedOptions = getSelectedOptions(options, selectedValue.id);
    onCitySelect(selectedValue, selectedOptions);
    onClose();
  };

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: 'rgba(24, 97, 191, 0.1)',
      },
  });

  const cityOptions = React.useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      const myOptions: Option[] = [];
      options.map((item) => {
        const id = item.id;
        const children = ('children' in item && item?.children) ?? [];
        if (id && id !== 'hot_cities' && children && children.length > 0) {
          children?.map(({ id, name, pinyin }) => {
            myOptions.push({
              id,
              name,
              pinyin,
              searchPinyin: pinyin?.replace(/[0-9]/g, ''),
            });
          });
        }
      });
      return myOptions;
    }
    return [];
  }, [options]);

  const search = React.useCallback(
    (value: string): Option[] => {
      const lowerCaseValue = value.replace(/[\x20-\x40]/g, '').toLowerCase();
      if (lowerCaseValue.length === 0) return [];
      const result: { id: string; name: string; pinyin: string }[] = [];
      const category = lowerCaseValue[0].match(/[a-z]/)
        ? 'searchPinyin'
        : 'name';
      for (const obj of cityOptions) {
        if (obj[category]?.startsWith(lowerCaseValue)) {
          result.push({ id: obj.id, name: obj.name, pinyin: obj.pinyin || '' });
          if (result.length >= 5) return result;
        }
      }
      return result;
    },
    [cityOptions],
  );

  const searchOptions: Option[] = React.useMemo(() => {
    return search(searchValue);
  }, [searchValue]);

  const hotCities = React.useMemo(() => {
    return HOT_CITIES.map((item) =>
      cityOptions.find((value) => value.name === item + '市'),
    ).slice(0, 8);
  }, [cityOptions]);

  const cityNamesByType = React.useMemo(() => {
    const cityObj: Record<string, ({ pinyin?: string } & Option)[]> = {};
    cityOptions.map((item) => {
      const type = item?.pinyin?.charAt(0).toUpperCase();
      if (type && item) {
        if (type in cityObj && cityObj[type]) {
          cityObj[type].push(item);
        } else {
          cityObj[type] = [item];
        }
      }
    });
    return Object.entries(cityObj)
      .map(([key, item]) => ({
        type: key,
        cities: item,
      }))
      .sort((a, b) => (a.type > b.type ? 1 : -1));
  }, [cityOptions]);

  React.useEffect(() => {
    onClose();
  }, [isMobile]);

  if (!isMobile) {
    return null;
  }

  return (
    <MobileDrawer
      anchor="right"
      open={cityPickerOpen}
      handleClose={onClose}
      PaperProps={{ className: 'w-full' }}
    >
      <div className="w-full h-full">
        <div className="flex items-center p-3.5 pr-6">
          <IconButton
            className="mr-3.5 p-0"
            onClick={onClose}
            sx={{ width: 'auto' }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Autocomplete
            value={value?.id === NATION_WIDE.id ? undefined : value}
            className="rounded-md w-full h-[45px] bg-[#f7f7f7]"
            options={searchOptions}
            noOptionsText="抱歉，无此城市的数据"
            onInputChange={(e, value) => {
              setSearchValue(value);
            }}
            filterOptions={(options) => options}
            onChange={(e, value, reason) => {
              if (reason === 'selectOption' && value) {
                handleSelect(value);
              }
            }}
            PopperComponent={styledPopper}
            autoHighlight={true}
            forcePopupIcon={false}
            selectOnFocus={false}
            disableClearable={true}
            blurOnSelect={true}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) =>
              !!option &&
              !!value &&
              !!option.name &&
              !!value.name &&
              option.name === value.name
            }
            renderInput={(params) => (
              <div
                className="w-full h-full text-black pl-2"
                ref={params.InputProps.ref}
              >
                <InputBase
                  placeholder="搜索城市"
                  className="w-full h-full"
                  inputProps={params.inputProps}
                />
              </div>
            )}
          />
        </div>
        {showNationWide && (
          <div className="text-sm text-[#333] px-3 mt-3.5">
            <button
              className="px-2.5 py-1 rounded-md border border-[#ccc]"
              onClick={() =>
                handleSelect({
                  id: NATION_WIDE.id,
                  name: NATION_WIDE.name,
                })
              }
            >
              {NATION_WIDE.name}
            </button>
          </div>
        )}
        {hotCities.length > 0 && (
          <div className="py-5 px-3 text-sm text-[#333]">
            <p className="mb-2.5 font-semibold">热门城市</p>
            <div className="grid grid-cols-4 gap-2.5 pr-5">
              {hotCities.map((item) => {
                if (!item) return;
                const { id, name, pinyin } = item;
                return (
                  <button
                    key={id}
                    className="px-2.5 py-1 rounded-md border border-[#ccc]"
                    onClick={() =>
                      handleSelect({
                        id,
                        name,
                        pinyin,
                      })
                    }
                  >
                    {endsWith(name, '市') ? name.slice(0, -1) : name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {cityNamesByType.length > 0 && (
          <>
            <ul className="fixed right-0 top-1/2 -translate-y-1/2 list-none m-0 pr-2 text-xs text-center text-[#333]">
              {cityNamesByType.map((item) => (
                <li
                  key={item.type}
                  onClick={() =>
                    document
                      .getElementById(`city-${item.type}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                >
                  {item.type}
                </li>
              ))}
            </ul>
            <>
              {cityNamesByType.map((item) => {
                const { type, cities } = item;
                return (
                  <div key={type} id={`city-${type}`}>
                    <p className="bg-[#f7f7f7] text-[#b2b2b2] px-3 py-1 text-xs">
                      {type}
                    </p>
                    <ul className="list-none m-0 text-sm text-[#333]">
                      {cities.map((city) => {
                        const { id, name, pinyin } = city;
                        return (
                          <li
                            key={id}
                            className="py-4 border-b px-3 border-[#d6e4ef]"
                            onClick={() =>
                              handleSelect({
                                id,
                                name,
                                pinyin,
                              })
                            }
                          >
                            {endsWith(name, '市') ? name.slice(0, -1) : name}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </>
          </>
        )}
      </div>
    </MobileDrawer>
  );
};

export default MobileCityPicker;
